import { useState, useEffect } from "react";
import { X, Ticket } from "lucide-react";
import { formatPrice } from "../../helper/utils";
import "./EntryTicketModal.css";

const DEFAULT_TICKET_TYPES = [
  "Regular",
  "Standard",
  "Couple",
  "VIP",
  "RSVP",
  "Others",
];

const EntryTicketModal = ({
  visible,
  onClose,
  onSubmit,
  initialTickets = [],
  preferredCurrency = "NGN",
}) => {
  const [tickets, setTickets] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const [selectedType, setSelectedType] = useState("");
  const [customType, setCustomType] = useState("");
  const [price, setPrice] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const currencyLabel = preferredCurrency === "NGN" ? "NGN(₦)" : "USD($)";

  useEffect(() => {
    if (Array.isArray(initialTickets) && initialTickets.length > 0) {
      setTickets(initialTickets);
    }
  }, [initialTickets]);

  if (!visible) return null;

  const finalType =
    selectedType === "Others" ? customType.trim() : selectedType;

  const validateAndAdd = () => {
    if (!finalType) {
      alert("Please select or enter a ticket type");
      return;
    }

    if (!price.trim()) {
      alert("Please enter a price");
      return;
    }

    const exists = tickets.some(
      (t) => t.type.toLowerCase() === finalType.toLowerCase()
    );
    if (exists) {
      alert(`"${finalType}" already exists`);
      return;
    }

    if (selectedType === "Others") {
      const isDefault = DEFAULT_TICKET_TYPES.slice(0, -1).some(
        (t) => t.toLowerCase() === finalType.toLowerCase()
      );
      if (isDefault) {
        alert(
          `"${finalType}" is a default type. Please select it from the list.`
        );
        return;
      }
    }
    setTickets((prev) => [...prev, { type: finalType, price }]);
    setSelectedType("");
    setCustomType("");
    setPrice("");
    setShowDropdown(false);
    setShowAddModal(false);
  };

  const handleDelete = (index) => {
    setTickets(tickets.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSubmit(tickets);
    onClose();
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedType("");
    setCustomType("");
    setPrice("");
    setShowDropdown(false);
  };

  return (
    <>
      <div className="etm-main-overlay" onClick={onClose}>
        <div
          className="etm-main-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="etm-main-header">
            <div className="sheet-grab-bar" />
            <button className="etm-close-btn" onClick={onClose}>
              <X size={24} color="#666" />
            </button>
          </div>

          <div className="etm-main-content">
            <h2 className="etm-main-title">Ticket (Entry Fee)</h2>
            <strong>Important notes on Ticket</strong>
            <p className="etm-main-subtitle">
              All entry fees would be forwarded to the provided account when
              requested.
            </p>
            <h2 className="etm-sub-title">Choose Ticket Types and Prices</h2>
            <p>Add different ticket types for your event!</p>

            <div className="etm-ticket-row">
              {tickets.map((ticket, index) => (
                <div key={index} className="etm-ticket-card">
                  <div className="etm-ticket-image-box">
                    <Ticket size={80} color="#FFA500" strokeWidth={1.5} />
                    <button
                      className="etm-delete-btn"
                      onClick={() => handleDelete(index)}
                      aria-label="Delete ticket"
                    >
                      <X size={16} strokeWidth={2.5} />
                    </button>
                  </div>

                  <p className="etm-ticket-price">
                    {formatPrice(ticket.price, preferredCurrency)}
                  </p>
                  <p className="etm-ticket-type">{ticket.type}</p>
                </div>
              ))}

              <div
                className="etm-ticket-card add-card"
                onClick={() => setShowAddModal(true)}
              >
                <div className="etm-ticket-image-box add">
                  <span>＋</span>
                </div>
                <p className="etm-add-text">
                  {tickets.length === 0 ? "Add Ticket" : "Add More"}
                </p>
              </div>
            </div>

            <div className="etm-main-actions">
              <button className="etm-secondary-btn" onClick={onClose}>
                Back
              </button>
              <button className="etm-primary-btn" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="etm-sheet-overlay" onClick={closeAddModal}>
          <div
            className="etm-bottom-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="etm-sheet-grab-bar" />

            <div className="etm-sheet-header">
              <h3>Add Ticket Type</h3>
              <button className="etm-sheet-btn" onClick={closeAddModal}>
                <X size={24} color="#666" />
              </button>
            </div>

            <div className="etm-sheet-content">
              <div className="etm-dropdown-container">
                <label className="etm-field-label">Ticket Type</label>
                <div
                  className="etm-dropdown-trigger"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <span
                    className={
                      selectedType
                        ? "dropdown-selected"
                        : "dropdown-placeholder"
                    }
                  >
                    {selectedType || "Select ticket type"}
                  </span>
                  <span className="etm-chevron">
                    {showDropdown ? "▲" : "▼"}
                  </span>
                </div>

                {showDropdown && (
                  <div className="etm-dropdown-list">
                    {DEFAULT_TICKET_TYPES.map((type) => {
                      const isDisabled =
                        type !== "Others" &&
                        tickets.some(
                          (t) => t.type.toLowerCase() === type.toLowerCase()
                        );

                      return (
                        <div
                          key={type}
                          className={`etm-dropdown-option ${
                            isDisabled ? "disabled" : ""
                          } ${selectedType === type ? "selected" : ""}`}
                          onClick={() => {
                            if (!isDisabled) {
                              setSelectedType(type);
                              if (type !== "Others") setCustomType("");
                              setShowDropdown(false);
                            }
                          }}
                        >
                          {type}
                          {isDisabled && " (Already added)"}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedType === "Others" && (
                <div className="etm-custom-input">
                  <label className="field-label">Custom Ticket Name</label>
                  <input
                    type="text"
                    placeholder="Enter custom ticket name"
                    value={customType}
                    onChange={(e) => setCustomType(e.target.value)}
                    className="etm-text-input"
                  />
                </div>
              )}

              <div className="etm-price-field">
                <label className="etm-field-label">Price</label>
                <div className="etm-price-input-wrapper">
                  <span className="etm-currency-prefix">{currencyLabel}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="1,000"
                    value={price}
                    onChange={(e) =>
                      setPrice(e.target.value.replace(/[^\d]/g, ""))
                    }
                    className="etm-price-input"
                  />
                </div>
                <p className="etm-price-note">
                  Note: For free ticket Enter 0 (zero) as the amount
                </p>
              </div>
            </div>

            <div className="etm-sheet-actions">
              <button className="etm-secondary-btn" onClick={closeAddModal}>
                Back
              </button>
              <button
                className="etm-primary-btn"
                onClick={validateAndAdd}
                disabled={!finalType || !price}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EntryTicketModal;
